import classNames from 'classnames';
import React from 'react';

import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {PasteEvent, SelectionPartition} from 'notebook/mediocre/EditorTypes';
import {Format, FormatData} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {INDENT_SIZE, ListType} from 'notebook/notes/lists/ListConstants';
import ListSymbol from 'notebook/notes/lists/ListSymbol';
import NoteConstants from 'notebook/notes/NoteConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ElementKeyboardEvent, Optional} from 'util/Types';

const {HEADER_1, HEADER_2, SUBHEADER_1, PARAGRAPH} = NoteConstants;
export interface NoteEditableProps extends MediocreSearchResultProps {
  cid: string;
  text: string;
  index: number;
  listType: Optional<ListType>;
  headerType: string;
  calculatedListIndex: number;
  indentation: number;
  locked: boolean;
  isProtected?: boolean;
  formats: Format;
  spellCheck: boolean;

  pasteHandler: (index: number, e: PasteEvent) => void;
  updateHandler: (
    index: number,
    e: {
      text: string;
      target: Element | EventTarget;
      formats: Format;
      selection?: BenchlingSelection;
    }
  ) => void;
  formatHandler: (
    indexInfo: {index: number},
    e: {
      format: string;
      partition: SelectionPartition;
      data?: FormatData;
    }
  ) => void;
  splitHandler: (
    index: number,
    e: SelectionPartition & {
      modifierKey: boolean;
      shiftKey: boolean;
      originalEvent: ElementKeyboardEvent;
    }
  ) => void;
  mergeUpHandler: (
    index: number,
    e: {
      anchor: boolean;
    }
  ) => void;
  moveHandler: (
    index: number,
    e: {
      up: boolean;
      beginning: boolean;
    }
  ) => void;
  tabHandler: (
    index: number,
    e: {
      partition: SelectionPartition;
      shiftKey: boolean;
      target: EventTarget;
    }
  ) => void;
  multiSelectHandler: (
    index: number,
    e: {
      up: boolean;
    }
  ) => void;
  keyDownHook: (index: number, e: ElementKeyboardEvent) => boolean;
  checkHandler: (index: number, checked: boolean) => void;
  storeState: (args: {selection: BenchlingSelection}) => void;
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: {selection: SelectionRestoreData}) => boolean;
}

class NoteEditable extends React.PureComponent<NoteEditableProps> {
  pasteHandler = (e: PasteEvent): void => {
    this.props.pasteHandler(this.props.index, e);
  };

  updateHandler = (e: {
    text: string;
    target: Element | EventTarget;
    formats: Format;
    selection?: BenchlingSelection;
  }): void => {
    this.props.updateHandler(this.props.index, e);
  };

  formatHandler = (e: {format: string; partition: SelectionPartition; data?: FormatData}): void => {
    this.props.formatHandler(
      {
        index: this.props.index,
      },
      e
    );
  };

  splitHandler = (
    e: SelectionPartition & {
      modifierKey: boolean;
      shiftKey: boolean;
      originalEvent: ElementKeyboardEvent;
    }
  ): void => {
    this.props.splitHandler(this.props.index, e);
  };

  mergeUpHandler = (e: {anchor: boolean}): void => {
    this.props.mergeUpHandler(this.props.index, e);
  };

  moveHandler = (e: {up: boolean; beginning: boolean}): void => {
    this.props.moveHandler(this.props.index, e);
  };

  tabHandler = (e: {partition: SelectionPartition; shiftKey: boolean; target: EventTarget}): void => {
    this.props.tabHandler(this.props.index, e);
  };

  multiSelectHandler = (e: {up: boolean}): void => {
    this.props.multiSelectHandler(this.props.index, e);
  };

  keyDownHook = (e: ElementKeyboardEvent): boolean => this.props.keyDownHook(this.props.index, e);

  override render(): JSX.Element {
    const noteClasses = classNames({
      'mediocre-noteEditable': true,
      'multi-editable': true,
      'is-header1': this.props.headerType === HEADER_1,
      'is-header2': this.props.headerType === HEADER_2,
      'is-subheader1': this.props.headerType === SUBHEADER_1,
      'is-paragraph': this.props.headerType === PARAGRAPH,
    });
    const indentationWidth = this.props.indentation * INDENT_SIZE;

    const listSymbol =
      this.props.listType != null ? (
        <ListSymbol
          index={this.props.index}
          listType={this.props.listType}
          calculatedListIndex={this.props.calculatedListIndex}
          indentation={this.props.indentation}
          checkHandler={this.props.checkHandler}
          // List symbols shouldn't be disabled for edit if the component is protected, only locked
          locked={this.props.locked}
        />
      ) : null;

    return (
      <div
        className={noteClasses}
        {...getTestProps({component: 'NoteEditable', element: 'root', index: this.props.index.toString()})}
      >
        <div
          className="mediocre-noteEditable-indenter"
          style={{width: indentationWidth}}
          {...getTestProps({component: 'NoteEditable', element: 'indenter'})}
        >
          {listSymbol}
        </div>
        <EditableComponent
          index={this.props.index}
          cid={this.props.cid}
          text={this.props.text}
          allowRichText={true}
          allowMultiline={false}
          allowAtSearch={true}
          allowSlashSearch={true}
          allowDefaultPaste={true}
          locked={this.props.locked}
          isProtected={this.props.isProtected}
          formats={this.props.formats}
          spellCheck={this.props.spellCheck}
          pasteHandler={this.pasteHandler}
          updateHandler={this.updateHandler}
          formatHandler={this.formatHandler}
          splitHandler={this.splitHandler}
          mergeUpHandler={this.mergeUpHandler}
          moveHandler={this.moveHandler}
          tabHandler={this.tabHandler}
          multiSelectHandler={this.multiSelectHandler}
          storeState={this.props.storeState}
          restoreState={this.props.restoreState}
          keyDownHook={this.keyDownHook}
          {...forwardSearchResultProps(this.props)}
        />
      </div>
    );
  }
}

export default NoteEditable;
